<div class="modal-container">
  <h2 mat-dialog-title>
    @if (viewMode === 'folders') {
      Browse Sources
    }
    @if (viewMode === 'folder-sources') {
      <button (click)="goBackToFolders()" class="back-button" mat-icon-button matTooltip="Back to folders">
        <mat-icon>arrow_back</mat-icon>
      </button>
      {{ selectedFolder?.name }}
    }
    @if (viewMode === 'search') {
      Search Results
    }
  </h2>
  <div class="search-container">
    <div class="search-wrapper">
      <form (ngSubmit)="search()" [formGroup]="searchForm">
        <input
          autocomplete="off"
          formControlName="query"
          matInput
          placeholder="Type to search sources..."
          />
      </form>
      <div class="search-buttons">
        <button
          (click)="clearSearch()"
          [disabled]="!searchForm.get('query')?.value"
          mat-icon-button
          >
          <mat-icon>close</mat-icon>
        </button>
        <button
          (click)="search()"
          [disabled]="!searchForm.get('query')?.value"
          color="primary"
          mat-icon-button
          >
          <mat-icon>search</mat-icon>
        </button>
      </div>
    </div>
  </div>

  <mat-dialog-content>

    <!-- FOLDER LIST VIEW -->
    @if (viewMode === 'folders') {
      @if (foldersLoading) {
        <div class="loading-container">
          <mat-progress-spinner
            [color]="'accent'"
            [diameter]="32"
            [mode]="'indeterminate'"
            class="spinner"
          ></mat-progress-spinner>
        </div>
      }
      @if (!foldersLoading) {
        @if (folders.length === 0) {
          <div class="empty-container">
            <mat-icon>folder_off</mat-icon>
            <p>No folders found</p>
          </div>
        }
        @if (folders.length > 0) {
          <ul class="folder-list">
            @for (folder of folders; track folder._id) {
              <li class="folder-list-item" (click)="selectFolder(folder)">
                <div class="folder-item-content">
                  <mat-icon class="folder-icon">folder</mat-icon>
                  <span class="folder-name">{{ folder.name }}</span>
                  @if (folder.approved) {
                    <mat-icon class="approval-icon" matTooltip="Approved">check_circle</mat-icon>
                  }
                </div>
                <div class="folder-item-meta">
                  @if (folder.sourceCount !== undefined) {
                    <span class="source-count">{{ folder.sourceCount }} {{ folder.sourceCount === 1 ? 'source' : 'sources' }}</span>
                  }
                  <mat-icon class="chevron-icon">chevron_right</mat-icon>
                </div>
              </li>
            }
          </ul>
        }
      }
    }

    <!-- FOLDER SOURCES VIEW -->
    @if (viewMode === 'folder-sources') {
      @if (folderSourcesLoading) {
        <div class="loading-container">
          <mat-progress-spinner
            [color]="'accent'"
            [diameter]="32"
            [mode]="'indeterminate'"
            class="spinner"
          ></mat-progress-spinner>
        </div>
      }
      @if (!folderSourcesLoading) {
        <div class="results-section">
          @if (folderSources.length === 0) {
            <div class="empty-container">
              <mat-icon>source</mat-icon>
              <p>No sources in this folder</p>
            </div>
          }
          @if (folderSources.length > 0) {
            <div>
              <mat-table #folderTable [dataSource]="folderSources">
                <ng-container matColumnDef="name">
                  <mat-header-cell *matHeaderCellDef>Source</mat-header-cell>
                  <mat-cell *matCellDef="let row">
                    <div class="d-flex align-items-center fs-14">
                      <mat-icon class="file-type-icon">{{ getFileTypeIcon(row) }}</mat-icon>
                      <div class="d-flex flex-col">
                        <span class="source-name">{{ row.name }}</span>
                        <span class="reference">{{ row.references }}</span>
                      </div>
                    </div>
                  </mat-cell>
                </ng-container>
                <ng-container matColumnDef="fileType">
                  <mat-header-cell *matHeaderCellDef class="file-type-header">Type</mat-header-cell>
                  <mat-cell *matCellDef="let row" class="file-type-cell">
                    @if (getFileType(row)) {
                      <span class="file-type-badge">{{ getFileType(row) }}</span>
                    }
                    @if (!getFileType(row)) {
                      <span class="no-file-text">—</span>
                    }
                  </mat-cell>
                </ng-container>
                <ng-container matColumnDef="actions">
                  <mat-header-cell *matHeaderCellDef class="justify-content-center max-width-20">
                    Actions
                  </mat-header-cell>
                  <mat-cell *matCellDef="let row" class="justify-content-center max-width-20">
                    <app-local-source-button
                      (sourceAdded)="onSourceAdded()"
                      [botId]="botId"
                      [source]="row"
                    ></app-local-source-button>
                  </mat-cell>
                </ng-container>
                <mat-header-row *matHeaderRowDef="['name', 'fileType', 'actions']"></mat-header-row>
                <mat-row
                  *matRowDef="let row; columns: ['name', 'fileType', 'actions']"
                  [class.selected]="isSourceAdded(row)"
                ></mat-row>
              </mat-table>
              <mat-paginator
                #folderPaginator
                (page)="handleFolderSourcesPageEvent($event)"
                [length]="folderSourcesPaging.total"
                [pageIndex]="folderSourcesPaging.page - 1"
                [pageSize]="10"
                aria-label="Select page"
                class="mat-elevation-z0"
              ></mat-paginator>
            </div>
          }
        </div>
      }
    }

    <!-- SEARCH RESULTS VIEW -->
    @if (viewMode === 'search') {
      @if (isSearchLoading) {
        <div class="loading-container">
          <mat-progress-spinner
            [color]="'accent'"
            [diameter]="32"
            [mode]="'indeterminate'"
            class="spinner"
          ></mat-progress-spinner>
        </div>
      }
      @if (!isSearchLoading) {
        <div class="results-section">
          @if (searchResults.total === 0) {
            <div class="empty-container">
              <mat-icon>search_off</mat-icon>
              <p>No results found</p>
            </div>
          }
          @if (searchResults.total > 0) {
            <div>
              <mat-table #table [dataSource]="searchResults.data">
                <ng-container matColumnDef="name">
                  <mat-header-cell *matHeaderCellDef>Source</mat-header-cell>
                  <mat-cell *matCellDef="let row">
                    <div (click)="onSourceClick(row)" class="d-flex align-items-center fs-14 clickable">
                      <mat-icon class="file-type-icon">{{ getFileTypeIcon(row) }}</mat-icon>
                      <div class="d-flex flex-col">
                        <span class="source-name">{{ row.name }}</span>
                        <span class="reference">{{ row.references }}</span>
                      </div>
                    </div>
                  </mat-cell>
                </ng-container>
                <ng-container matColumnDef="fileType">
                  <mat-header-cell *matHeaderCellDef class="file-type-header">Type</mat-header-cell>
                  <mat-cell *matCellDef="let row" class="file-type-cell">
                    @if (getFileType(row)) {
                      <span class="file-type-badge">{{ getFileType(row) }}</span>
                    }
                    @if (!getFileType(row)) {
                      <span class="no-file-text">—</span>
                    }
                  </mat-cell>
                </ng-container>
                <ng-container matColumnDef="actions">
                  <mat-header-cell *matHeaderCellDef class="justify-content-center max-width-20">
                    Actions
                  </mat-header-cell>
                  <mat-cell *matCellDef="let row" class="justify-content-center max-width-20">
                    <app-local-source-button
                      (sourceAdded)="onSourceAdded()"
                      [botId]="botId"
                      [source]="row"
                    ></app-local-source-button>
                  </mat-cell>
                </ng-container>
                <mat-header-row *matHeaderRowDef="['name', 'fileType', 'actions']"></mat-header-row>
                <mat-row
                  *matRowDef="let row; columns: ['name', 'fileType', 'actions']"
                  [class.selected]="isSourceAdded(row)"
                ></mat-row>
              </mat-table>
              <mat-paginator
                #paginator
                (page)="handlePageEvent($event)"
                [length]="searchResults.total"
                [pageIndex]="paging.page - 1"
                [pageSize]="10"
                aria-label="Select page"
                class="mat-elevation-z0"
              ></mat-paginator>
            </div>
          }
        </div>
      }
    }

  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button color="primary" mat-button mat-dialog-close>Cancel</button>
  </mat-dialog-actions>
</div>
